import { useEffect, useRef, useState } from 'react';
import { toast } from "sonner";

export default function DidEmbed({ agentId = 'v2_agt_LYpauVih' }) {
  const [hasError, setHasError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const initialized = useRef(false);
  
  // Use a unique target ID for each agent to ensure clean DOM separation
  const TARGET_ID = `did-agent-target-${agentId}`;

  useEffect(() => {
    // We only run this effect when agentId changes or on initial mount
    // We use a cleanup function to ensure we don't leave artifacts
    
    const loadAgent = () => {
        // 1. Force cleanup of any residuals globally and locally
        const existingScripts = document.querySelectorAll('script[data-name="did-agent"]');
        existingScripts.forEach(s => s.remove());
        
        const targetDiv = document.getElementById(TARGET_ID);
        if (targetDiv) targetDiv.innerHTML = '';

        // 2. Construct Script EXACTLY as provided in snippet
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://agent.d-id.com/v2/index.js';
        
        // Required attributes
        script.dataset.mode = 'full';
        script.dataset.clientKey = 'Z29vZ2xlLW9hdXRoMnwxMTgzNjY5MzEyNzY3Mzg4OTc0Nzg6QmVNNHB5R19tMjJiQmlQMktPQ2M4';
        script.dataset.agentId = agentId;
        script.dataset.name = 'did-agent';
        // script.dataset.monitor = 'true'; // Removed to prevent chat creation errors
        script.dataset.targetId = TARGET_ID;

        // 3. Error Listeners
        script.onerror = () => {
            setHasError(true);
            setErrorMsg("Script failed to load network resource.");
        };

        document.body.appendChild(script);
    };

    // Allow a brief tick for React to render the div before we inject
    const timer = setTimeout(loadAgent, 50);

    // Global error handler for the specific D-ID 403
    const handleDidError = (e) => {
        if (e.message && e.message.includes('403')) {
            setHasError(true);
            setErrorMsg("Access Denied (403). Domain not authorized.");
            console.error("D-ID 403 Error: Domain whitelisting required in D-ID Studio.");
        }
    };
    window.addEventListener('error', handleDidError);

    return () => {
        clearTimeout(timer);
        window.removeEventListener('error', handleDidError);
        const existingScripts = document.querySelectorAll('script[data-name="did-agent"]');
        existingScripts.forEach(s => s.remove());
    };
  }, [agentId, TARGET_ID]);

  return (
    <div className="relative w-full h-full bg-slate-100 rounded-xl overflow-hidden">
      {/* Target Div - Unique ID per agent */}
      <div id={TARGET_ID} className="w-full h-full absolute inset-0 z-10" />

      {/* Loading State */}
      {!hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 z-0">
           <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin mb-2"></div>
           <p className="text-xs font-medium">Connecting to Agent...</p>
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20 text-center p-6">
           <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-3">
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
           </div>
           <h3 className="text-slate-800 font-semibold">Connection Failed</h3>
           <p className="text-slate-500 text-sm mt-1 max-w-xs mx-auto mb-4">
             {errorMsg}
           </p>
           {errorMsg.includes("403") && (
               <div className="text-xs bg-slate-50 p-3 rounded border border-slate-200 text-slate-600 text-left">
                   <strong>Action Required:</strong><br/>
                   1. Go to D-ID Studio > Agents<br/>
                   2. Add this domain to "Allowed Domains":<br/>
                   <code className="bg-slate-200 px-1 rounded mt-1 inline-block">{window.location.hostname}</code>
               </div>
           )}
        </div>
      )}
    </div>
  );
}
